import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  ChevronUp,
  Save,
  RotateCcw,
  History,
  Plus,
  Trash2,
  Loader2,
  Lock,
  Unlock,
  FileText,
  Clock,
  Users,
  CheckCircle2,
  Circle,
  Download,
  Mail,
  FilePlus,
} from "lucide-react";
import api, { getUser } from "@/lib/api";
import RTAnalysisPanel from "@/components/RTAnalysisPanel";
import CarePlanAiAnalysisPanel from "@/components/CarePlanAiAnalysisPanel";
import BlockHelpButton from "@/components/BlockHelpButton";
import DepositProgress from "@/components/DepositProgress";

/**
 * `CarePlanBlocksEditor` — editor por bloco do Módulo Plano de Cuidados.
 *
 * Cada bloco é uma secção colapsável. Ao clicar em "Guardar bloco":
 *  - Backend faz PUT /care-plans/{lead}/blocks com só esse bloco
 *  - Master gera automaticamente uma nova versão (V2, V3, ...)
 *
 * Se a versão actual estiver `is_frozen=true`, todos os botões de gravar
 * ficam bloqueados (uma versão definitiva não pode ser mutada). Uma nova
 * edição a partir daqui teria de reabrir/reiniciar o ciclo — comportamento
 * a definir em iteração seguinte.
 */
const BLOCK_LABELS = {
  // === Grupo 1 · Identificação ===
  service_identification: {
    label: "Identificação do serviço",
    type: "object_kv",
    group: "identificacao",
    fields: [
      { key: "service_type", label: "Serviço", type: "text",
        placeholder: "ex: Apoio domiciliário · Continuidade", autofill: true },
      { key: "status", label: "Estado", type: "select", options: [
        { value: "Ativo", label: "Ativo" },
        { value: "Suspenso", label: "Suspenso" },
        { value: "Terminado", label: "Terminado" },
      ] },
      { key: "schedule", label: "Horário", type: "text", placeholder: "08:30–13:00", autofill: true },
      { key: "frequency", label: "Frequência", type: "text", placeholder: "Segunda a sexta", autofill: true },
      { key: "start_date", label: "Início", type: "text", placeholder: "DD/MM/AAAA", autofill: true },
    ],
  },

  // === Grupo 2 · Perfil da pessoa & Estado clínico ===
  critical_summary: {
    label: "Resumo crítico · LER ANTES DA PRESTAÇÃO",
    type: "text",
    group: "perfil",
    hint: "Notas críticas de segurança/saúde. O ajudante deve ler antes de começar o serviço.",
  },
  person_profile: {
    label: "Perfil da pessoa",
    type: "structured",
    group: "perfil",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", dimension: "", info: "" },
    columns: [
      { key: "dimension", label: "Dimensão", type: "text", minWidth: 180,
        placeholder: "Como gosta de ser tratada / Rotina / Interesses…" },
      { key: "info", label: "Informação relevante", type: "text", minWidth: 320 },
    ],
  },
  functional_assessment: {
    label: "Avaliação funcional",
    type: "structured",
    group: "perfil",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", area: "", level: "", observation: "" },
    columns: [
      { key: "area", label: "Área", type: "select", minWidth: 140, options: [
        { value: "Higiene", label: "Higiene" },
        { value: "Vestir", label: "Vestir" },
        { value: "Mobilidade", label: "Mobilidade" },
        { value: "Alimentação", label: "Alimentação" },
        { value: "Continência", label: "Continência" },
        { value: "Comunicação", label: "Comunicação" },
        { value: "Cognição", label: "Cognição funcional" },
      ]},
      { key: "level", label: "Nível", type: "select", minWidth: 140, options: [
        { value: "Autónomo", label: "Autónomo" },
        { value: "Supervisão", label: "Supervisão" },
        { value: "Apoio parcial", label: "Apoio parcial" },
        { value: "Apoio total", label: "Apoio total" },
      ]},
      { key: "observation", label: "Observação", type: "text", minWidth: 300 },
    ],
  },

  // === Grupo 3 · Objetivos & Necessidades ===
  objectives: { label: "Objetivos gerais do plano", type: "list_str", group: "objetivos" },
  priorities: { label: "Prioridades", type: "list_str", group: "objetivos" },
  needs_objectives: {
    label: "Necessidades · Objetivos · Intervenções",
    type: "structured",
    group: "objetivos",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", need: "", objective: "", intervention: "", frequency: "" },
    columns: [
      { key: "need", label: "Necessidade", type: "text", minWidth: 140 },
      { key: "objective", label: "Objetivo", type: "text", minWidth: 200 },
      { key: "intervention", label: "Intervenção", type: "text", minWidth: 240 },
      { key: "frequency", label: "Frequência", type: "text", minWidth: 100,
        placeholder: "Dias úteis / Sempre / Diário…" },
    ],
  },
  recommendations: { label: "Recomendações", type: "list_str", group: "objetivos" },

  // === Grupo 4 · Rotinas & Plano semanal ===
  routines: {
    label: "Rotina Diária (Hora | Atividade | Instruções)",
    type: "structured",
    group: "rotinas",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", time: "", description: "", category: "", notes: "" },
    columns: [
      { key: "time", label: "Hora", type: "text", minWidth: 80, placeholder: "08:30" },
      { key: "description", label: "Atividade", type: "text", minWidth: 200 },
      { key: "notes", label: "Instruções", type: "text", minWidth: 300 },
    ],
  },
  weekly_plan: {
    label: "Plano semanal (Tarefa × Dias)",
    type: "structured",
    group: "rotinas",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", task: "",
      mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
    columns: [
      { key: "task", label: "Tarefa", type: "text", minWidth: 200 },
      { key: "mon", label: "Seg", type: "checkbox", minWidth: 40 },
      { key: "tue", label: "Ter", type: "checkbox", minWidth: 40 },
      { key: "wed", label: "Qua", type: "checkbox", minWidth: 40 },
      { key: "thu", label: "Qui", type: "checkbox", minWidth: 40 },
      { key: "fri", label: "Sex", type: "checkbox", minWidth: 40 },
      { key: "sat", label: "Sáb", type: "checkbox", minWidth: 40 },
      { key: "sun", label: "Dom", type: "checkbox", minWidth: 40 },
    ],
  },

  // === Grupo 5 · Alimentação, medicação, riscos ===
  nutrition_hydration: {
    label: "Alimentação e hidratação",
    type: "text",
    group: "clinico",
    hint: "Preferências, restrições, hidratação a incentivar.",
  },
  medication_level: {
    label: "Medicação — nível de apoio",
    type: "text",
    group: "clinico",
    hint: 'Ex.: "Lembrete e supervisão apenas. Medicação preparada pela família."',
  },
  medication_notes: {
    label: "Medicação — notas adicionais",
    type: "text",
    group: "clinico",
  },
  risks: {
    label: "Riscos e medidas preventivas",
    type: "structured",
    group: "clinico",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", description: "", severity: "medium",
      mitigation: "", escalate_when: "" },
    columns: [
      { key: "description", label: "Risco", type: "text", minWidth: 160 },
      { key: "severity", label: "Nível", type: "select", minWidth: 100, options: [
        { value: "low", label: "Baixo" },
        { value: "medium", label: "Moderado" },
        { value: "high", label: "Alto" },
      ]},
      { key: "mitigation", label: "Medidas", type: "text", minWidth: 220 },
      { key: "escalate_when", label: "Escalar quando", type: "text", minWidth: 200 },
    ],
  },

  // === Grupo 6 · Comunicação, acompanhamento & contactos ===
  communication_escalation: {
    label: "Comunicação e escalamento",
    type: "structured",
    group: "gestao",
    showBeneficiary: false,
    defaultRow: { scenario_type: "normal", description: "" },
    columns: [
      { key: "scenario_type", label: "Tipo", type: "select", minWidth: 130, options: [
        { value: "normal", label: "Normal (registo)" },
        { value: "alteracao", label: "Alteração relevante" },
        { value: "ocorrencia", label: "Ocorrência" },
        { value: "urgencia", label: "Urgência" },
        { value: "emergencia", label: "Emergência" },
      ]},
      { key: "description", label: "Descrição do cenário", type: "text", minWidth: 400 },
    ],
  },
  followup_plan: {
    label: "Plano de acompanhamento",
    type: "structured",
    group: "gestao",
    showBeneficiary: false,
    defaultRow: { action: "", responsible: "", periodicity: "" },
    columns: [
      { key: "action", label: "Ação", type: "text", minWidth: 220 },
      { key: "responsible", label: "Responsável", type: "select", minWidth: 160, options: [
        { value: "Gestor de Cliente", label: "Gestor de Cliente" },
        { value: "Direção Técnica", label: "Direção Técnica" },
        { value: "Equipa técnica", label: "Equipa técnica" },
      ]},
      { key: "periodicity", label: "Periodicidade", type: "text", minWidth: 160,
        placeholder: "2 contactos/mês, Trimestral, Contínua…" },
    ],
  },
  contacts: {
    label: "Contactos operacionais",
    type: "structured",
    group: "gestao",
    showBeneficiary: false,
    defaultRow: { label: "", name: "", phone: "", email: "" },
    columns: [
      { key: "label", label: "Rótulo", type: "text", minWidth: 140,
        placeholder: "Médico, Farmácia, Filha…" },
      { key: "name", label: "Nome", type: "text", minWidth: 180 },
      { key: "phone", label: "Telefone", type: "text", minWidth: 130 },
      { key: "email", label: "Email", type: "email", minWidth: 180 },
    ],
  },

  // === Grupo 7 · Vista AF (Ajudante Familiar) ===
  caregiver_tratar_por: {
    label: "AF · Tratar por",
    type: "text_short",
    group: "af",
    hint: "Ex.: D. Maria",
  },
  caregiver_mobility_summary: {
    label: "AF · Mobilidade (resumo)",
    type: "text_short",
    group: "af",
    hint: "Ex.: Andarilho",
  },
  caregiver_communication_summary: {
    label: "AF · Comunicação (resumo)",
    type: "text_short",
    group: "af",
    hint: "Ex.: Falar de frente",
  },
  caregiver_preference_summary: {
    label: "AF · Preferência-chave",
    type: "text_short",
    group: "af",
    hint: "Ex.: Banho de manhã",
  },
  caregiver_person_desc: {
    label: "AF · Descrição da pessoa",
    type: "text",
    group: "af",
    hint: "Texto livre sobre autonomia, preferências, coisas a respeitar.",
  },
  caregiver_before_start: {
    label: "AF · Atenção antes de começar",
    type: "text",
    group: "af",
    hint: "Riscos e observações críticas que o AF deve saber antes.",
  },
  caregiver_daily_tasks: {
    label: "AF · O que fazer hoje (Hora × Tarefa)",
    type: "structured",
    group: "af",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", time: "", what_to_do: "" },
    columns: [
      { key: "time", label: "Hora", type: "text", minWidth: 80, placeholder: "08:30" },
      { key: "what_to_do", label: "O que fazer", type: "text", minWidth: 400 },
    ],
  },
  care_delivery: {
    label: "AF · Como prestar os principais cuidados",
    type: "structured",
    group: "af",
    showBeneficiary: true,
    defaultRow: { beneficiary: "both", care_area: "", do: "", dont: "" },
    columns: [
      { key: "care_area", label: "Cuidado", type: "text", minWidth: 140,
        placeholder: "Higiene, Mobilidade, Alimentação…" },
      { key: "do", label: "O que fazer", type: "text", minWidth: 260 },
      { key: "dont", label: "Não fazer / Comunicar", type: "text", minWidth: 260 },
    ],
  },
  caregiver_before_ending: {
    label: "AF · Antes de terminar o serviço",
    type: "list_str",
    group: "af",
  },
  caregiver_when_to_contact: {
    label: "AF · Quando contactar a Caring",
    type: "text",
    group: "af",
  },

  // === Grupo 8 · Vista Cliente / Família ===
  client_summary_intro: {
    label: "Cliente · Introdução do plano",
    type: "text",
    group: "cliente",
    hint: "Frase curta que abre o documento partilhado com a família.",
  },
  client_preferences_kv: {
    label: "Cliente · Preferências que vamos respeitar",
    type: "object_kv",
    group: "cliente",
    fields: [
      { key: "higiene", label: "Higiene", type: "text_short", placeholder: "Ex.: De manhã" },
      { key: "vestuario", label: "Vestuário", type: "text_short", placeholder: "Ex.: Escolha própria" },
      { key: "alimentacao", label: "Alimentação", type: "text_short", placeholder: "Ex.: Prefere peixe" },
      { key: "comunicacao", label: "Comunicação", type: "text_short", placeholder: "Ex.: Frente a frente" },
      { key: "bem_estar", label: "Bem-estar", type: "text_short", placeholder: "Ex.: Rádio e conversa" },
      { key: "ambiente", label: "Ambiente", type: "text_short", placeholder: "Ex.: Objetos no lugar habitual" },
      { key: "ritmo", label: "Ritmo", type: "text_short", placeholder: "Ex.: Sem pressa" },
      { key: "cafe", label: "Café", type: "text_short", placeholder: "Ex.: Curto após almoço" },
    ],
  },
  client_special_attention: {
    label: "Cliente · Pontos com especial atenção",
    type: "structured",
    group: "cliente",
    showBeneficiary: false,
    defaultRow: { title: "", description: "" },
    columns: [
      { key: "title", label: "Título", type: "text", minWidth: 200,
        placeholder: "Mobilidade segura, Alimentação e hidratação…" },
      { key: "description", label: "Descrição", type: "text", minWidth: 400 },
    ],
  },
  client_team_members: {
    label: "Cliente · A sua equipa (auto se vazio)",
    type: "structured",
    group: "cliente",
    showBeneficiary: false,
    defaultRow: { role: "", name: "", paper: "" },
    columns: [
      { key: "role", label: "Função", type: "text", minWidth: 200 },
      { key: "name", label: "Nome", type: "text", minWidth: 200 },
      { key: "paper", label: "Papel", type: "text", minWidth: 320 },
    ],
  },
  client_followup_intro: {
    label: "Cliente · Introdução ao acompanhamento",
    type: "text",
    group: "cliente",
  },
  client_followup_stages: {
    label: "Cliente · Etapas do acompanhamento",
    type: "structured",
    group: "cliente",
    showBeneficiary: false,
    defaultRow: { stage: "", what_happens: "" },
    columns: [
      { key: "stage", label: "Etapa", type: "text", minWidth: 180,
        placeholder: "1. Plano, 2. Prestação…" },
      { key: "what_happens", label: "O que acontece", type: "text", minWidth: 400 },
    ],
  },
  family_notes: {
    label: "Mensagem à Família",
    type: "text",
    group: "cliente",
  },

  // === Grupo 9 · Notas internas ===
  caregiver_profile: {
    label: "Perfil do Ajudante Familiar (recrutamento)",
    type: "text",
    group: "notas",
  },
  internal_notes: {
    label: "Notas internas (equipa)",
    type: "text",
    group: "notas",
  },
};

const BLOCK_GROUPS = [
  { key: "identificacao", label: "Identificação do serviço", icon: "info" },
  { key: "perfil", label: "Perfil da pessoa & Estado clínico", icon: "user" },
  { key: "objetivos", label: "Objetivos & Necessidades", icon: "target" },
  { key: "rotinas", label: "Rotinas & Plano semanal", icon: "clock" },
  { key: "clinico", label: "Alimentação · Medicação · Riscos", icon: "clipboard" },
  { key: "gestao", label: "Comunicação · Acompanhamento · Contactos", icon: "phone" },
  { key: "af", label: "Ajudante Familiar (vista)", icon: "hard-hat" },
  { key: "cliente", label: "Cliente / Família (vista)", icon: "heart" },
  { key: "notas", label: "Notas & Recrutamento", icon: "file" },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function TextBlock({ blockKey, value, onChange, disabled }) {
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={4}
      data-testid={`cp-block-${blockKey}-textarea`}
      className="w-full text-sm bg-card border border-border rounded-lg px-3 py-2 focus:border-primary focus:outline-none disabled:bg-muted disabled:cursor-not-allowed"
      placeholder="Escreva o conteúdo aqui..."
    />
  );
}

function TextShortBlock({ blockKey, value, onChange, disabled, placeholder }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder || ""}
      data-testid={`cp-block-${blockKey}-input`}
      className="w-full text-sm bg-card border border-border rounded-lg px-3 py-1.5 focus:border-primary focus:outline-none disabled:bg-muted disabled:cursor-not-allowed"
    />
  );
}

/**
 * Bloco de campos fixos (objeto com chaves conhecidas).
 * Usado para `service_identification` e `client_preferences_kv`.
 *
 * Cada `spec.fields[i]` é `{ key, label, type: 'text'|'text_short'|'select', options?, placeholder?, autofill? }`.
 * Se `autofill=true`, mostramos um badge "auto" — o backend preenche
 * quando o valor está vazio (o utilizador pode sobrepor).
 */
function ObjectKVBlock({ blockKey, value, onChange, disabled, fields, autofillValues }) {
  const obj = (value && typeof value === "object" && !Array.isArray(value)) ? value : {};
  const setField = (k, v) => onChange({ ...obj, [k]: v });
  const preview = autofillValues || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`cp-block-${blockKey}-object`}>
      {fields.map((f) => {
        const val = obj[f.key] ?? "";
        // 27/02/2026 — quando o campo tem badge "auto" e está vazio,
        // mostramos o valor calculado pelo backend como placeholder.
        // Se o campo já tem valor gravado, o utilizador vê o que gravou.
        const autoValue = (f.autofill && !val) ? (preview[f.key] || "") : "";
        const effectivePlaceholder = autoValue || f.placeholder || "";
        return (
          <label key={f.key} className="flex flex-col gap-1" data-testid={`cp-block-${blockKey}-field-${f.key}`}>
            <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5">
              {f.label}
              {f.autofill && (
                <span className="text-[9px] uppercase tracking-widest font-semibold text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 rounded-full px-1.5 py-0.5"
                      title="Preenchido automaticamente pelo sistema quando vazio">
                  auto
                </span>
              )}
            </span>
            {f.type === "select" ? (
              <select
                value={val}
                onChange={(e) => setField(f.key, e.target.value)}
                disabled={disabled}
                className="text-sm bg-card border border-border rounded-lg px-2 py-1.5 focus:border-primary focus:outline-none disabled:bg-muted"
              >
                <option value="">{`— ${f.label} —`}</option>
                {(f.options || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={val}
                onChange={(e) => setField(f.key, e.target.value)}
                disabled={disabled}
                placeholder={effectivePlaceholder}
                data-testid={`cp-block-${blockKey}-field-${f.key}-input`}
                data-autofill-value={autoValue || undefined}
                className={`text-sm bg-card border border-border rounded-lg px-2 py-1.5 focus:border-primary focus:outline-none disabled:bg-muted ${autoValue && !val ? "placeholder:text-[hsl(var(--warning))] placeholder:italic" : ""}`}
              />
            )}
          </label>
        );
      })}
    </div>
  );
}

function ListStrBlock({ blockKey, items, onChange, disabled }) {
  const list = Array.isArray(items) ? items : [];
  const setAt = (idx, val) => onChange(list.map((it, i) => (i === idx ? val : it)));
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const add = () => onChange([...list, ""]);
  // Auto-grow: reseta altura para "auto" e depois define a altura do scroll
  // para que o textarea expanda com o conteúdo (sem scroll interno).
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 40)}px`;
  };
  return (
    <div className="flex flex-col gap-2" data-testid={`cp-block-${blockKey}-list`}>
      {list.map((it, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <textarea
            value={it || ""}
            onChange={(e) => { setAt(idx, e.target.value); autoGrow(e.target); }}
            ref={autoGrow}
            rows={1}
            disabled={disabled}
            data-testid={`cp-block-${blockKey}-item-${idx}`}
            className="flex-1 text-sm bg-card border border-border rounded-lg px-3 py-2 focus:border-primary focus:outline-none disabled:bg-muted resize-none leading-relaxed min-h-[40px] overflow-hidden"
            placeholder="Novo item..."
          />
          <button
            onClick={() => remove(idx)}
            disabled={disabled}
            className="p-1.5 mt-1 text-muted-foreground hover:text-[hsl(var(--danger))] disabled:opacity-40 flex-shrink-0"
            data-testid={`cp-block-${blockKey}-remove-${idx}`}
            title="Remover"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        disabled={disabled}
        data-testid={`cp-block-${blockKey}-add`}
        className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary disabled:opacity-40 w-fit"
      >
        <Plus className="w-3.5 h-3.5" /> Adicionar
      </button>
    </div>
  );
}


/**
 * Selector do beneficiário aplicável a uma linha de bloco estruturado.
 *
 * Só é renderizado quando existir mais de 1 beneficiário no lead
 * (`beneficiaries.length >= 2`). Para leads com 1 beneficiário devolve
 * `null` e o valor é sempre "both" internamente.
 */
function BeneficiarySelector({ value, onChange, beneficiaries, disabled, testId }) {
  if (!beneficiaries || beneficiaries.length < 2) return null;
  return (
    <select
      value={value || "both"}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testId}
      className="text-[11px] bg-card border border-border rounded-full px-2 py-1 focus:border-primary focus:outline-none disabled:bg-muted"
      title="A quem se aplica esta linha"
    >
      <option value="both">{`🧑‍🤝‍🧑 Ambos`}</option>
      <option value="1">{`👤 ${beneficiaries[0]?.name || "B1"}`}</option>
      <option value="2">{`👤 ${beneficiaries[1]?.name || "B2"}`}</option>
    </select>
  );
}


/**
 * Bloco estruturado (tabela) genérico — usa um schema declarativo para
 * saber que colunas renderizar. Suporta os 4 blocos com estrutura:
 * activities, routines, risks, contacts.
 */
function StructuredBlock({
  blockKey,
  items,
  onChange,
  columns,       // [{key, label, type: 'text'|'select', options?, width?}]
  showBeneficiary = false,
  beneficiaries = [],
  disabled,
  defaultRow,
}) {
  const list = Array.isArray(items) ? items : [];
  const setAt = (idx, patch) =>
    onChange(list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const add = () => onChange([...list, { ...defaultRow }]);
  return (
    <div className="flex flex-col gap-2" data-testid={`cp-block-${blockKey}-struct`}>
      {list.length === 0 && (
        <div className="text-xs text-muted-foreground py-2">
          Sem entradas. Clique em "Adicionar linha" para começar.
        </div>
      )}
      {list.map((row, idx) => (
        <div
          key={idx}
          data-testid={`cp-block-${blockKey}-row-${idx}`}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 p-2"
        >
          {showBeneficiary && (
            <BeneficiarySelector
              value={row.beneficiary}
              onChange={(v) => setAt(idx, { beneficiary: v })}
              beneficiaries={beneficiaries}
              disabled={disabled}
              testId={`cp-block-${blockKey}-row-${idx}-beneficiary`}
            />
          )}
          {columns.map((col) => (
            <div key={col.key} style={{ flex: col.width || 1, minWidth: col.minWidth || 120 }}>
              {col.type === "select" ? (
                <select
                  value={row[col.key] || ""}
                  onChange={(e) => setAt(idx, { [col.key]: e.target.value })}
                  disabled={disabled}
                  data-testid={`cp-block-${blockKey}-row-${idx}-${col.key}`}
                  className="w-full text-xs bg-card border border-border rounded-lg px-2 py-1.5 focus:border-primary focus:outline-none disabled:bg-muted"
                >
                  <option value="">{`— ${col.label} —`}</option>
                  {col.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : col.type === "checkbox" ? (
                <label
                  className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider cursor-pointer select-none"
                  title={col.label}
                >
                  <input
                    type="checkbox"
                    checked={!!row[col.key]}
                    onChange={(e) => setAt(idx, { [col.key]: e.target.checked })}
                    disabled={disabled}
                    data-testid={`cp-block-${blockKey}-row-${idx}-${col.key}`}
                    className="w-4 h-4 accent-[#1E3A8A] cursor-pointer"
                  />
                  <span className="text-[9px] font-semibold text-muted-foreground">{col.label}</span>
                </label>
              ) : (
                <input
                  type={col.type === "email" ? "email" : "text"}
                  value={row[col.key] || ""}
                  onChange={(e) => setAt(idx, { [col.key]: e.target.value })}
                  disabled={disabled}
                  placeholder={col.placeholder || col.label}
                  data-testid={`cp-block-${blockKey}-row-${idx}-${col.key}`}
                  className="w-full text-xs bg-card border border-border rounded-lg px-2 py-1.5 focus:border-primary focus:outline-none disabled:bg-muted"
                />
              )}
            </div>
          ))}
          <button
            onClick={() => remove(idx)}
            disabled={disabled}
            data-testid={`cp-block-${blockKey}-row-${idx}-remove`}
            className="p-1.5 text-muted-foreground hover:text-[hsl(var(--danger))] disabled:opacity-40"
            title="Remover linha"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        disabled={disabled}
        data-testid={`cp-block-${blockKey}-add-row`}
        className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary disabled:opacity-40 w-fit"
      >
        <Plus className="w-3.5 h-3.5" /> Adicionar linha
      </button>
    </div>
  );
}

/**
 * Grupo colapsável de blocos relacionados.
 *
 * Cada grupo mostra um cabeçalho com contador dos blocos preenchidos e
 * expande para uma grid 2-col com os `BlockSection`.
 */
function GroupSection({ group, blocks, blocksData, saving, disabled, onSave, beneficiaries, autofillPreview, canEdit = true }) {
  const [expanded, setExpanded] = useState(group.key === "identificacao");
  // Contador de blocos com conteúdo (visual only)
  const filled = blocks.reduce((acc, [key, spec]) => {
    const v = blocksData[key];
    let has = false;
    if (spec.type === "text" || spec.type === "text_short") {
      has = !!(v && String(v).trim());
    } else if (spec.type === "list_str") {
      has = Array.isArray(v) && v.filter(Boolean).length > 0;
    } else if (spec.type === "structured") {
      has = Array.isArray(v) && v.length > 0;
    } else if (spec.type === "object_kv") {
      has = v && typeof v === "object" &&
        Object.values(v).some((val) => val && String(val).trim());
    }
    return has ? acc + 1 : acc;
  }, 0);
  return (
    <div
      className="border border-border rounded-2xl bg-muted/40 overflow-hidden"
      data-testid={`cp-group-${group.key}`}
    >
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-muted transition-colors"
        data-testid={`cp-group-${group.key}-toggle`}
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-foreground text-[11px] font-bold">
            {filled}/{blocks.length}
          </span>
          <span className="text-sm font-semibold text-primary uppercase tracking-wider">
            {group.label}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-[hsl(var(--warning))]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[hsl(var(--warning))]" />
        )}
      </button>
      {expanded && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-4 pt-2 border-t border-[#EBE0C7]">
          {blocks.map(([key, spec]) => (
            <BlockSection
              key={key}
              blockKey={key}
              spec={spec}
              initialValue={blocksData[key]}
              saving={saving}
              disabled={disabled}
              onSave={onSave}
              beneficiaries={beneficiaries}
              autofillValues={(autofillPreview || {})[key]}
              canEdit={canEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
}



function BlockSection({ blockKey, spec, initialValue, saving, disabled, onSave, beneficiaries, autofillValues, canEdit = true }) {
  const [expanded, setExpanded] = useState(true);
  const [value, setValue] = useState(initialValue);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    // Se o master recarregar (nova versão de outro utilizador, por ex.),
    // sincroniza o estado local — mas apenas quando o utilizador não tem
    // alterações pendentes.
    if (!dirty) setValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);
  const update = (v) => {
    setValue(v);
    setDirty(true);
  };
  const doSave = async () => {
    // Normaliza list_str filtrando strings vazias
    let payload = value;
    if (spec.type === "list_str" && Array.isArray(payload)) {
      payload = payload.map((s) => (s || "").trim()).filter(Boolean);
    }
    // Normaliza structured filtrando rows completamente vazias
    if (spec.type === "structured" && Array.isArray(payload)) {
      payload = payload.filter((row) =>
        Object.entries(row).some(
          ([k, v]) => k !== "beneficiary" &&
            v !== false && v !== null && v !== undefined &&
            String(v).trim() !== "",
        ),
      );
    }
    // Normaliza object_kv (garante que é objeto, nunca undefined/null)
    if (spec.type === "object_kv" && (payload == null || typeof payload !== "object")) {
      payload = {};
    }
    // Normaliza text_short/text — envia string vazia se undefined
    if ((spec.type === "text" || spec.type === "text_short") && payload == null) {
      payload = "";
    }
    await onSave(blockKey, payload);
    setDirty(false);
  };
  const reset = () => {
    setValue(initialValue);
    setDirty(false);
  };
  return (
    <div
      className={`border border-border rounded-xl bg-card ${(spec.type === "structured" || spec.type === "object_kv") ? "lg:col-span-2" : ""}`}
      data-testid={`cp-block-${blockKey}`}
    >
      {/* 04/08/2026 — This header used to be a <button> containing the
          BlockHelpButton, which produced invalid nested-button markup
          (React DOM warning + help click bubbling to the parent). It's
          now a role="button" div that toggles on click/Enter/Space, so
          the BlockHelpButton stays as a real <button> without nesting. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 rounded-t-xl cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        data-testid={`cp-block-${blockKey}-toggle`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {spec.label}
          <BlockHelpButton blockKey={blockKey} />
          {spec.showBeneficiary && beneficiaries?.length >= 2 && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary bg-[hsl(var(--info-bg))] border border-[#B9DAF3] rounded-full px-2 py-0.5"
              title="Este bloco suporta separação por beneficiário"
            >
              <Users className="w-2.5 h-2.5" /> B1 / B2 / Ambos
            </span>
          )}
          {dirty && (
            <span
              data-testid={`cp-block-${blockKey}-dirty`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 rounded-full px-2 py-0.5"
            >
              Não guardado
            </span>
          )}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </div>
      {expanded && (
        <div className="p-4 pt-2 border-t border-[#F0EBDD]">
          {spec.hint && (
            <div className="mb-2 text-[11px] italic text-muted-foreground">{spec.hint}</div>
          )}
          {spec.type === "text" && (
            <TextBlock
              blockKey={blockKey}
              value={value}
              onChange={update}
              disabled={disabled}
            />
          )}
          {spec.type === "text_short" && (
            <TextShortBlock
              blockKey={blockKey}
              value={value}
              onChange={update}
              disabled={disabled}
              placeholder={spec.hint}
            />
          )}
          {spec.type === "object_kv" && (
            <ObjectKVBlock
              blockKey={blockKey}
              value={value}
              onChange={update}
              disabled={disabled}
              fields={spec.fields}
              autofillValues={autofillValues}
            />
          )}
          {spec.type === "list_str" && (
            <ListStrBlock
              blockKey={blockKey}
              items={value}
              onChange={update}
              disabled={disabled}
            />
          )}
          {spec.type === "structured" && (
            <StructuredBlock
              blockKey={blockKey}
              items={value}
              onChange={update}
              columns={spec.columns}
              showBeneficiary={spec.showBeneficiary}
              beneficiaries={beneficiaries}
              disabled={disabled}
              defaultRow={spec.defaultRow}
            />
          )}
          {/* 27/02/2026 — Save/Cancel controls só aparecem quando o
              utilizador tem permissão de edição. Leitores (gestor/consultor)
              não veem "Guardar bloco" nem "Cancelar" na DOM. */}
          {canEdit && (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={doSave}
                disabled={saving || disabled || !dirty}
                data-testid={`cp-block-${blockKey}-save`}
                className="inline-flex items-center gap-1.5 text-xs bg-primary hover:bg-primary text-primary-foreground rounded-full px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Guardar bloco
              </button>
              {dirty && !saving && (
                <button
                  onClick={reset}
                  data-testid={`cp-block-${blockKey}-reset`}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-muted-foreground"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Cancelar
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Card único de aprovação da Direção Técnica.
 *
 * A card mostra:
 * - Título + estado (aprovado / pendente)
 * - **Dropdown de atribuição** — o Gestor escolhe QUE utilizador DT
 *   vai validar o plano.
 * - Botão "Aprovar" visível apenas para o user actualmente atribuído
 *   (ou admin) e apenas enquanto a versão não estiver congelada.
 *
 * Se a versão já estiver `is_frozen`, a card fica visual + botão
 * "Reabrir" no editor (só admin/gestor — reabre criando nova versão).
 */
function ApprovalStep({
  stepKey,
  label,
  description,
  entry,
  users,               // lista pré-filtrada por role p/ este step
  assigneeId,          // id do utilizador atribuído (do lead)
  assigneeName,        // nome (para read-only)
  onAssignChange,      // (userId | null) => void
  onApprove,           // () => void
  canAssign,           // pode alterar o assignee
  canApprove,          // pode carregar em "Aprovar"
  isFrozen,
}) {
  const approved = !!entry?.approved;
  return (
    <div
      className={`flex flex-col gap-2 rounded-xl border p-3 min-h-[130px] ${
        approved
          ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25"
          : "bg-card border-border"
      }`}
      data-testid={`cp-editor-step-${stepKey}`}
    >
      <div className="flex items-center gap-2">
        {approved ? (
          <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" />
        ) : (
          <Circle className="w-4 h-4 text-muted-foreground" />
        )}
        <span className="text-sm font-semibold text-foreground">{label}</span>
        {approved && (
          <span
            data-testid={`cp-editor-step-${stepKey}-approved-badge`}
            className="ml-auto text-[10px] uppercase tracking-widest font-semibold text-[hsl(var(--success))]"
          >
            Aprovado
          </span>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground leading-snug">{description}</div>

      {/* Dropdown de atribuição */}
      {canAssign && !approved && !isFrozen ? (
        <select
          value={assigneeId || ""}
          onChange={(e) => onAssignChange(e.target.value || null)}
          data-testid={`cp-editor-step-${stepKey}-assign-select`}
          className="text-xs bg-card border border-border rounded-lg px-2 py-1.5 focus:border-primary focus:outline-none"
        >
          <option value="">— Atribuir utilizador —</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} {u.role !== stepKey && u.role !== "admin" ? `(${u.role})` : ""}
            </option>
          ))}
        </select>
      ) : (
        <div className="text-[11px] text-muted-foreground">
          <span className="opacity-60">Atribuído a: </span>
          <b>{assigneeName || entry?.by?.name || "—"}</b>
          {approved && entry?.at && (
            <span className="opacity-60"> · {new Date(entry.at).toLocaleDateString("pt-PT")}</span>
          )}
        </div>
      )}

      {/* Botão de aprovação (só visível para o assignee + role match) */}
      {!approved && canApprove && !isFrozen && (
        <button
          onClick={onApprove}
          data-testid={`cp-editor-approve-${stepKey}-btn`}
          className="mt-auto inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary text-primary-foreground rounded-full px-3 py-1.5"
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar como {label}
        </button>
      )}
      {!approved && !canApprove && assigneeId && !isFrozen && (
        <div className="mt-auto text-[10px] italic text-muted-foreground">
          Aguarda aprovação do utilizador atribuído.
        </div>
      )}
    </div>
  );
}


export default function CarePlanBlocksEditor({ leadId, lead, initialCarePlan, onLeadRefresh }) {
  const user = getUser();
  // 28/02/2026 — Só a Direção Técnica pode editar o formulário completo
  // do Plano de Cuidados. Consultor/Gestor continuam a poder LER
  // (não é passado disabled=true — a lógica `disabled` também considera
  // ``isFrozen``). Aqui bloqueamos a EDIÇÃO por role.
  const canEdit =
    user && ["admin", "direcao", "dt"].includes(user.role);

  // Beneficiários lidos do próprio lead — nunca duplicados no master. Se
  // não vier `lead` (retro-compat com callers antigos), o array fica vazio
  // e o switcher B1/B2 nem sequer é renderizado (comportamento seguro).
  // não vier `lead` (retro-compat com callers antigos), o array fica vazio
  // e o switcher B1/B2 nem sequer é renderizado (comportamento seguro).
  const beneficiaries = useMemo(() => {
    const q = (lead && lead.qualification) || {};
    const b1 = q.cared_person || {};
    const b2 = q.cared_person_2 || {};
    const out = [];
    if (b1.name) out.push({ id: "1", name: b1.name });
    if (q.has_second_beneficiary && b2.name) out.push({ id: "2", name: b2.name });
    return out;
  }, [lead]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [master, setMaster] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  // 27/02/2026 — auto-fill preview vindo do backend
  // (GET /care-plans/{id} → autofill_preview.service_identification).
  // Usado nos campos com badge "auto" para mostrar o valor calculado
  // do lead como placeholder mesmo quando o utilizador ainda não gravou.
  const [autofillPreview, setAutofillPreview] = useState({});
  const [versionsList, setVersionsList] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Docx download busy state (por vista)
  const [docxBusy, setDocxBusy] = useState(null); // "client" | "caregiver" | "internal" | "client_summary" | null
  const [sendingEmail, setSendingEmail] = useState(false);
  // Users disponíveis para atribuição por role — carregado 1x
  const [usersByRole, setUsersByRole] = useState({ dt: [] });

  const canAssign = user?.role === "admin" || user?.role === "gestor";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    // Carrega utilizadores DT (Direção Técnica) para popular o dropdown
    // de atribuição do único passo de aprovação do Plano de Cuidados.
    if (!canAssign) return;
    api
      .get("/users")
      .then((r) => {
        const all = (r.data || []).filter((u) => u.active);
        setUsersByRole({
          dt: all.filter((u) => ["direcao", "dt"].includes(u.role)),
        });
      })
      .catch(() => {});
  }, [canAssign]);

  const assignStep = async (step, userId) => {
    // step = "dt" (único passo desde 28/02/2026)
    const pool = usersByRole.dt;
    const pick = pool.find((u) => u.id === userId) || null;
    const fields = {
      assigned_care_plan_dt_id: userId || null,
      assigned_care_plan_dt_name: pick?.name || null,
    };
    try {
      await api.patch(`/leads/${leadId}`, fields);
      toast.success(pick ? `${step.toUpperCase()} atribuído: ${pick.name}` : `${step.toUpperCase()} desatribuído`);
      // Refresca o lead para o parent reactualizar os assignees
      onLeadRefresh && onLeadRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atribuir");
    }
  };

  const downloadDocxView = async (view) => {
    if (docxBusy) return;
    setDocxBusy(view);
    try {
      const cli = (lead?.qualification?.contact?.name || "cliente").replace(/\s+/g, "-");
      const label = {
        internal: "equipa-interna",
        caregiver: "ajudante-familiar",
        client: "cliente-definitivo",
        client_summary: "cliente-panoramica",
      }[view];
      // Import lazy para não crescer bundle desnecessariamente
      const { downloadAuthedFile } = await import("@/lib/api");
      await downloadAuthedFile(
        `/care-plans/${leadId}/docx?view=${view}`,
        `plano-de-cuidados-${label}-${cli}.docx`,
        "attachment",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a descarregar .docx");
    } finally {
      setDocxBusy(null);
    }
  };

  // 27/02/2026 — Download PDF do Plano de Cuidados. Complementa o docx
  // (que já suporta 4 vistas). O PDF suporta 3 variantes no backend:
  //   • client    — cliente/família (sem horas por tarefa)
  //   • caregiver — Ajudante Familiar (tarefas diárias com HORAS + alertas
  //                 operacionais, sem info clínica sensível)
  //   • internal  — vista clínica completa da equipa
  // Uso: `.pdf Cliente` → cliente; `.pdf AF` → ajudante familiar;
  //      `.pdf Equipa` (opcional) → equipa técnica.
  const downloadPdfVariant = async (variant) => {
    if (docxBusy) return;
    setDocxBusy(`pdf-${variant}`);
    try {
      const cli = (lead?.qualification?.contact?.name || "cliente").replace(/\s+/g, "-");
      const label = {
        client: "cliente",
        caregiver: "af",
        internal: "equipa-interna",
        "dt-validation": "ficha-dt",
      }[variant] || variant;
      const { downloadAuthedFile } = await import("@/lib/api");
      // 28/02/2026 · Fase 3 — a Ficha DT vive num endpoint próprio
      // (``/leads/{id}/care-plan/dt-validation-pdf``), não é um variant do
      // gerador principal. Todos os outros PDFs usam o endpoint unificado.
      const path = variant === "dt-validation"
        ? `/leads/${leadId}/care-plan/dt-validation-pdf`
        : `/leads/${leadId}/care-plan/pdf?variant=${variant}`;
      await downloadAuthedFile(
        path,
        `plano-de-cuidados-${label}-${cli}.pdf`,
        "attachment",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a descarregar PDF");
    } finally {
      setDocxBusy(null);
    }
  };

  /** 28/02/2026 · Fase 5 — exportar o processo técnico completo em JSON.
   * ``include_pic=true`` (default) exige aprovação DT; ``false`` exporta
   * apenas a Qualificação Técnica. */
  const exportTechnicalProcess = async (includePic = true) => {
    if (docxBusy) return;
    setDocxBusy(`json-${includePic ? "full" : "qt"}`);
    try {
      const { downloadAuthedFile } = await import("@/lib/api");
      await downloadAuthedFile(
        `/leads/${leadId}/technical-process/export?include_pic=${includePic ? "true" : "false"}`,
        `processo-tecnico-${leadId}.json`,
        "attachment",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a exportar processo técnico");
    } finally {
      setDocxBusy(null);
    }
  };

  /** 28/02/2026 · Fase 4 — criar uma Revisão sobre o PIC actualmente
   * validado. Prompt para motivo (obrigatório, mínimo 5 chars). */
  const createRevision = async () => {
    if (docxBusy) return;
    const motive = window.prompt(
      "Criar Revisão do PIC\n\n" +
      "Motivo da revisão (obrigatório):",
      "",
    );
    if (motive === null) return;
    const trimmed = (motive || "").trim();
    if (trimmed.length < 5) {
      toast.error("Motivo demasiado curto (mínimo 5 caracteres).");
      return;
    }
    setDocxBusy("revise");
    try {
      const { data } = await api.post(`/care-plans/${leadId}/revise`, { reason: trimmed });
      toast.success(`Nova Revisão criada · V${data?.current_version_number || "?"}`);
      onLeadRefresh && onLeadRefresh();
      // Recarregar imediatamente para saltar para a versão editável
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a criar Revisão");
    } finally {
      setDocxBusy(null);
    }
  };

  const sendClientFinalEmail = async () => {
    if (sendingEmail) return;
    const confirmed = window.confirm(
      "Enviar o Plano de Cuidados definitivo ao cliente por email?\n\n" +
      "Será anexado o documento .docx com selos de verificação.",
    );
    if (!confirmed) return;
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/care-plans/${leadId}/send-client-final`);
      toast.success(
        `Plano de Cuidados enviado ao cliente (${data?.recipient || "email do lead"}).`,
      );
      onLeadRefresh && onLeadRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar plano ao cliente");
    } finally {
      setSendingEmail(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // 28/02/2026 — Só quem tem permissão de EDIT chama o `ensure`
      // (idempotente mas escreve). Leitores (gestor/consultor) só
      // fazem GET — evita 403 forbidden no toast quando abrem a aba.
      if (canEdit) {
        const seed = initialCarePlan
          ? {
              objectives: initialCarePlan.objectives || [],
              priorities: initialCarePlan.priorities || [],
              recommendations: initialCarePlan.recommendations || [],
              caregiver_profile: initialCarePlan.caregiver_profile || "",
              activities: initialCarePlan.activities || [],
            }
          : {};
        await api.post(`/care-plans/${leadId}/ensure`, { initial_blocks: seed });
      }
      const { data } = await api.get(`/care-plans/${leadId}`);
      if (!data.exists) {
        setMaster(null);
        setCurrentVersion(null);
        setAutofillPreview({});
      } else {
        setMaster(data.master);
        setCurrentVersion(data.current_version);
        setAutofillPreview(data.autofill_preview || {});
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar plano");
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      const { data } = await api.get(`/care-plans/${leadId}/versions`);
      setVersionsList(data.versions || []);
    } catch {
      /* silencioso */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const saveBlock = async (blockKey, value) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/care-plans/${leadId}/blocks`, {
        blocks: { [blockKey]: value },
        reason: `edit:${blockKey}`,
      });
      setCurrentVersion(data.current_version);
      // Refresca contagem de versões
      setMaster((m) => (m ? { ...m, current_version_number: data.current_version_number } : m));
      toast.success(`Bloco "${BLOCK_LABELS[blockKey].label}" guardado → V${data.current_version_number}`);
      if (historyOpen) loadVersions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar bloco");
    } finally {
      setSaving(false);
    }
  };

  const approveAs = async (role) => {
    const label = { dt: "Direção Técnica" }[role] || role;
    if (!window.confirm(`Confirmas a aprovação como ${label} da versão actual?`)) return;
    try {
      const { data } = await api.post(`/care-plans/${leadId}/approve`, { role });
      setCurrentVersion(data.current_version);
      const nowFrozen = !!data.current_version?.is_frozen;
      toast.success(
        nowFrozen
          ? `Aprovado por ${label} · Versão ${data.current_version.version_label} DEFINITIVA`
          : `Aprovado por ${label}`,
      );
      if (historyOpen) loadVersions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aprovar");
    }
  };

  // Reabrir a versão actual para edição (admin/gestor).
  // Backend: POST /care-plans/{lead}/reopen — des-congela + reset aprovações.
  // A versão mantém o mesmo label (V1/V2/…); edições posteriores criam
  // automaticamente uma nova versão como já acontecia.
  const canReopen = user?.role === "admin" || user?.role === "gestor";
  const reopenVersion = async () => {
    if (!window.confirm(
      "Reabrir a versão actual para edição?\n\n" +
      "A aprovação da DT será anulada e terá de ser refeita. " +
      "Utilizadores/Gestores atribuídos são preservados."
    )) return;
    try {
      const { data } = await api.post(`/care-plans/${leadId}/reopen`, {});
      setCurrentVersion(data.current_version);
      toast.success(
        `Versão ${data.current_version.version_label} reaberta para edição · aprovações resetadas`,
      );
      if (historyOpen) loadVersions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reabrir versão");
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground">
        A carregar Módulo Plano de Cuidados…
      </div>
    );
  }

  if (!master || !currentVersion) {
    // 27/02/2026 — Leitor sem master (ainda não foi criado pela DT).
    // Damos um empty-state amigável em vez de void.
    if (!canEdit) {
      return (
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="care-plan-blocks-editor-empty-viewer">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            Módulo Plano de Cuidados · Direção Técnica
          </div>
          <div className="rounded-xl border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
            O Plano de Cuidados ainda não foi iniciado pela Direção Técnica.
            Quando a DT começar a preencher o formulário, esta secção passa a
            mostrar o conteúdo em modo leitura.
          </div>
        </div>
      );
    }
    return null; // Editor com role de edição — o painel de aprovações trata do caso.
  }

  const blocks = currentVersion.blocks || {};
  const isFrozen = !!currentVersion.is_frozen;
  const disabled = !canEdit || isFrozen;

  return (
    <div
      className="bg-card border border-border rounded-2xl p-6 lg:p-8"
      data-testid="care-plan-blocks-editor"
    >
      {/* 27/02/2026 — Banner de "modo leitura" para roles não-DT.
          O formulário completo (com tarefas diárias + horas) só é editável
          pela Direção Técnica. Outros perfis vêem tudo mas os inputs ficam
          disabled (via `disabled` prop já propagado). */}
      {!canEdit && (
        <div
          data-testid="care-plan-readonly-banner"
          className="mb-4 rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] px-4 py-2.5 text-xs text-[hsl(var(--warning))] flex items-center gap-2"
        >
          <Lock className="w-3.5 h-3.5 shrink-0" />
          <span>
            <b>Modo leitura</b> — apenas a Direção Técnica pode editar o Plano
            de Cuidados. Podes consultar o conteúdo e descarregar os exports
            disponíveis.
          </span>
        </div>
      )}
      {/* RT Analysis Panel — DESACTIVADO (25/02/2026 · pedido do
          utilizador). Feature escondida até nova ordem. */}
      {false && <RTAnalysisPanel leadId={leadId} onApplied={load} />}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            Módulo Plano de Cuidados · Editor por bloco
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-ui text-lg text-foreground font-semibold">
              Versão actual: {currentVersion.version_label}
            </h3>
            {isFrozen ? (
              <span
                data-testid="cp-editor-frozen-badge"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 rounded-full px-2.5 py-0.5"
              >
                <Lock className="w-3 h-3" /> Definitiva — DT aprovou
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 rounded-full px-2.5 py-0.5">
                Rascunho editável
              </span>
            )}
            {isFrozen && canReopen && (
              <button
                type="button"
                onClick={reopenVersion}
                data-testid="cp-editor-reopen-btn"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary bg-card border border-primary rounded-full px-3 py-1 hover:bg-primary hover:text-primary-foreground transition-colors"
                title="Anula as aprovações e permite editar novamente esta versão"
              >
                <Unlock className="w-3 h-3" /> Reabrir para edição
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Cada edição a um bloco gera automaticamente uma nova versão.{" "}
            {isFrozen && (
              canReopen
                ? "A versão actual está congelada — clica em Reabrir para edição para desbloquear."
                : "A versão actual está congelada — pede a um admin/gestor para reabrir para novas edições."
            )}
          </p>
          {/* Controles de aprovação — o utilizador aprova a versão actual
              (DT) directamente daqui, sem sair do editor.
              28/02/2026: aprovação bloqueada até proposta aceite +
              caução totalmente paga (gate operacional). */}
          {(() => {
            const proposalAccepted = !!(lead?.acceptance?.accepted);
            const deposit = lead?.deposit || {};
            const depositPlanTotal = Number(deposit?.plan?.total_amount) || 0;
            const depositPaidTotal = (deposit?.payments || []).reduce(
              (s, p) => s + (Number(p?.amount) || 0), 0
            );
            const depositFullyPaid =
              depositPlanTotal > 0 && depositPaidTotal + 0.005 >= depositPlanTotal;
            const gateOk = proposalAccepted && depositFullyPaid;
            return (
              <>
                {!gateOk && !isFrozen && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-3" data-testid="care-plan-dt-approve-gate">
                    <div>
                      <div className="text-xs font-semibold text-amber-900">
                        Aprovação da DT ainda bloqueada
                      </div>
                      <ul className="text-xs text-amber-800 mt-1 space-y-0.5 list-disc list-inside">
                        {!proposalAccepted && (
                          <li>Falta a <b>aceitação da proposta</b> pelo cliente.</li>
                        )}
                        {!depositFullyPaid && (
                          <li>Falta a <b>caução estar totalmente paga</b>.</li>
                        )}
                      </ul>
                    </div>
                    {!depositFullyPaid && depositPlanTotal > 0 && (
                      <DepositProgress deposit={deposit} testid="care-plan-dt-deposit-progress" />
                    )}
                  </div>
                )}
                <div className="mt-3 grid grid-cols-1 gap-3">
                  {[
                    {
                      key: "dt",
                      label: "Direção Técnica",
                      description: "Validação técnica clínica — único aprovador do Plano de Cuidados.",
                      users: usersByRole.dt,
                      assigneeId: lead?.assigned_care_plan_dt_id,
                      assigneeName: lead?.assigned_care_plan_dt_name,
                      allowRoles: ["admin", "dt", "direcao"],
                    },
                  ].map((st) => {
                    const entry = (currentVersion.approvals || {})[st.key] || {};
                    const canApproveThis =
                      st.allowRoles.includes(user?.role) &&
                      (isAdmin || user?.id === st.assigneeId) &&
                      gateOk;
                    return (
                      <ApprovalStep
                        key={st.key}
                        stepKey={st.key}
                        label={st.label}
                        description={st.description}
                        entry={entry}
                        users={st.users}
                        assigneeId={st.assigneeId}
                        assigneeName={st.assigneeName}
                        onAssignChange={(uid) => assignStep(st.key, uid)}
                        onApprove={() => approveAs(st.key)}
                        canAssign={canAssign}
                        canApprove={canApproveThis}
                        isFrozen={isFrozen}
                      />
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* Painel de Análise IA — só disponível em rascunho (visível
              sempre, mas o botão fica disabled quando isFrozen=true).
              27/02/2026 — passamos canEdit para esconder os CTAs de
              analyze/apply em modo leitura (evita 403 forbidden toast). */}
          <CarePlanAiAnalysisPanel
            leadId={leadId}
            currentVersion={currentVersion}
            canEdit={canEdit}
            onApplied={(updatedVersion) => {
              setCurrentVersion(updatedVersion);
              setMaster((m) => m ? { ...m, current_version_number: updatedVersion.version_number } : m);
            }}
          />

          {/* Plano de Cuidados em Word (.docx). Fluxo em 3 fases:
              1. Rascunho: só Panorâmica (client_summary) — visão global
                 do que a Caring vai atingir. Sempre disponível.
              2. Validado (is_frozen): Ajudante Familiar + Equipa Interna
                 desbloqueiam para uso operacional.
              3. Definitivo entregue: só quando (a) proposta aceite +
                 (b) contract_data + (c) kickstart pago + (d) is_frozen.
                 Aí o botão "Cliente Definitivo" desbloqueia e permite
                 envio manual ou automático por email. */}
          {(() => {
            const proposalAccepted = !!lead?.acceptance?.accepted_at;
            const hasContractData = !!lead?.contract_data;
            const kickstartPaid = !!lead?.deposit?.kickstart_paid;
            const canDeliverFinal = isFrozen && proposalAccepted && hasContractData && kickstartPaid;
            const gates = [
              { key: "frozen", label: "DT validou", ok: isFrozen },
              { key: "accepted", label: "Proposta aceite", ok: proposalAccepted },
              { key: "contract", label: "Dados de contrato submetidos", ok: hasContractData },
              { key: "kickstart", label: "Pagamento de arranque confirmado", ok: kickstartPaid },
            ];
            return (
              <div
                className="mt-4 rounded-xl border border-border bg-muted/40 px-4 py-3"
                data-testid="care-plan-docx-panel"
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] font-semibold mb-2">
                  📄 Plano de Cuidados (Word · editável)
                </div>

                {/* Fila de gates para o Cliente definitivo */}
                <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="care-plan-gates">
                  {gates.map((g) => (
                    <div
                      key={g.key}
                      data-testid={`care-plan-gate-${g.key}`}
                      className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 border ${
                        g.ok
                          ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25 text-[hsl(var(--success))]"
                          : "bg-card border-border text-[hsl(var(--warning))]"
                      }`}
                    >
                      {g.ok ? (
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      ) : (
                        <Circle className="w-3.5 h-3.5 shrink-0" />
                      )}
                      <span className="text-[11px] font-semibold leading-tight">{g.label}</span>
                    </div>
                  ))}
                </div>

                <div className="text-xs text-muted-foreground mb-3 leading-snug">
                  {canDeliverFinal ? (
                    <><b className="text-[hsl(var(--success))]">Plano definitivo pronto</b> — pode ser descarregado e enviado ao cliente por email com os selos de verificação.</>
                  ) : isFrozen ? (
                    <><b>Plano validado (equipa)</b> — Ajudante Familiar e Equipa Interna disponíveis. O documento definitivo para o Cliente só desbloqueia após aceite + contrato + pagamento de arranque.</>
                  ) : (
                    <><b>Rascunho</b> — só a Panorâmica global do Cliente pode ser descarregada. As restantes vistas desbloqueiam quando a Direção Técnica aprovar.</>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {(() => {
                    // Roles com acesso técnico ao PIC — DT / Gestor /
                    // Admin. Podem descarregar AF + Equipa Interna já em
                    // rascunho (precisam para trabalhar antes do freeze).
                    const isTechRole =
                      isAdmin ||
                      ["direcao", "dt", "gestor"].includes(user?.role);
                    const items = [
                      // ---- Grupo Cliente ----
                      {
                        group: "Para o cliente",
                        entries: [
                          {
                            key: "docx-client-summary",
                            label: ".docx Panorâmica (Cliente)",
                            badge: "RASCUNHO",
                            action: () => downloadDocxView("client_summary"),
                            available: true,
                            hint: "Panorâmica global · para partilhar com o cliente antes do contrato.",
                          },
                          {
                            key: "docx-client",
                            label: ".docx Definitivo (Cliente)",
                            badge: canDeliverFinal ? "✓ VALIDADO" : null,
                            action: () => downloadDocxView("client"),
                            available: canDeliverFinal,
                            hint: canDeliverFinal
                              ? "Documento definitivo · pronto a partilhar."
                              : "Desbloqueia após validação DT + aceite + contrato + arranque.",
                          },
                          {
                            key: "pdf-client",
                            label: ".pdf Cliente",
                            action: () => downloadPdfVariant("client"),
                            available: true,
                            hint: "PDF orientado por objetivos globais · sem horas por tarefa.",
                          },
                        ],
                      },
                      // ---- Grupo Equipa Caring ----
                      {
                        group: "Para a equipa Caring",
                        show: isTechRole,
                        entries: [
                          {
                            key: "docx-caregiver",
                            label: ".docx Ajudante Familiar",
                            action: () => downloadDocxView("caregiver"),
                            available: isTechRole,
                            hint: "Guião operacional · tarefas por hora + alertas.",
                          },
                          {
                            key: "docx-internal",
                            label: ".docx Equipa Interna",
                            action: () => downloadDocxView("internal"),
                            available: isTechRole,
                            hint: "Vista completa com notas clínicas · uso interno.",
                          },
                          {
                            key: "pdf-caregiver",
                            label: ".pdf Ajudante Familiar",
                            action: () => downloadPdfVariant("caregiver"),
                            available: isTechRole,
                            hint: "PDF operacional com tarefas diárias e horas.",
                          },
                          {
                            key: "pdf-internal",
                            label: ".pdf Equipa Interna",
                            action: () => downloadPdfVariant("internal"),
                            available: isTechRole,
                            hint: "PDF completo · estado clínico, riscos, diagnósticos.",
                          },
                        ],
                      },
                      // ---- Grupo Direção Técnica ----
                      {
                        group: "Direção Técnica",
                        show: true,
                        entries: [
                          {
                            key: "pdf-dt-validation",
                            label: "Ficha DT (imprimível A4)",
                            badge: "IMPRIMIR",
                            action: () => downloadPdfVariant("dt-validation"),
                            available: true,
                            hint: "Checkboxes por secção + espaço amplo para escrita à mão.",
                          },
                        ],
                      },
                      // ---- Grupo JSON ----
                      {
                        group: "Exportar JSON",
                        show: isTechRole,
                        entries: [
                          {
                            key: "json-full",
                            label: "Processo completo",
                            badge: canDeliverFinal || isFrozen ? null : "AGUARDA DT",
                            action: () => exportTechnicalProcess(true),
                            available: canDeliverFinal || isFrozen,
                            hint: "Todo o processo técnico · schema caring_processo_tecnico v1.0.",
                          },
                          {
                            key: "json-qt",
                            label: "Só Qualificação Técnica",
                            action: () => exportTechnicalProcess(false),
                            available: true,
                            hint: "Exporta apenas a QT · sempre disponível.",
                          },
                        ],
                      },
                    ].filter((g) => g.show !== false);

                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid="care-plan-generate-doc-menu"
                            className="inline-flex items-center gap-2 text-sm bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-4 py-2 font-semibold"
                          >
                            <Download className="w-4 h-4" />
                            Gerar documento
                            <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          className="w-[340px] max-h-[70vh] overflow-y-auto"
                          data-testid="care-plan-generate-doc-content"
                        >
                          {items.map((grp, gi) => (
                            <React.Fragment key={grp.group}>
                              {gi > 0 && <DropdownMenuSeparator />}
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                {grp.group}
                              </DropdownMenuLabel>
                              {grp.entries.map((e) => (
                                <DropdownMenuItem
                                  key={e.key}
                                  disabled={!e.available || !!docxBusy}
                                  onSelect={(evt) => {
                                    evt.preventDefault();
                                    if (e.available && !docxBusy) e.action();
                                  }}
                                  data-testid={`care-plan-doc-menu-${e.key}`}
                                  className="flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                                >
                                  <div className="w-full flex items-center gap-2">
                                    {e.available ? (
                                      docxBusy && docxBusy.includes(e.key) ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                                      ) : (
                                        <Download className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                                      )
                                    ) : (
                                      <Lock className="w-3.5 h-3.5 shrink-0 text-muted-foreground/60" />
                                    )}
                                    <span className={`text-sm ${e.available ? "font-medium" : "text-muted-foreground"}`}>
                                      {e.label}
                                    </span>
                                    {e.badge && (
                                      <span className={`ml-auto text-[9px] uppercase tracking-widest font-semibold rounded-full px-1.5 py-0.5 ${
                                        e.badge.startsWith("✓")
                                          ? "text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/40"
                                          : e.badge === "AGUARDA DT"
                                          ? "text-muted-foreground bg-muted border border-border"
                                          : "text-[#8F4F0A] bg-[hsl(var(--warning-bg))] border border-[#FCD9B0]"
                                      }`}>
                                        {e.badge}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] leading-snug text-muted-foreground pl-6">
                                    {e.hint}
                                  </div>
                                </DropdownMenuItem>
                              ))}
                            </React.Fragment>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  })()}

                  {isFrozen && (isAdmin || user?.role === "direcao" || user?.role === "dt") && (
                    <button
                      onClick={createRevision}
                      disabled={docxBusy === "revise"}
                      data-testid="care-plan-create-revision-btn"
                      title="Criar uma Revisão do PIC actual · abre nova versão editável (V+1)."
                      className="inline-flex items-center gap-1.5 text-xs bg-card hover:bg-muted border border-border text-primary rounded-full px-3 py-1.5"
                    >
                      {docxBusy === "revise" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus className="w-3.5 h-3.5" />}
                      Criar Revisão do PIC
                    </button>
                  )}

                  {canDeliverFinal && (
                    <button
                      onClick={() => sendClientFinalEmail()}
                      disabled={sendingEmail}
                      data-testid="care-plan-send-client-final-btn"
                      title="Enviar o Plano de Cuidados definitivo ao cliente por email (com selos de verificação)"
                      className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--warning))] hover:bg-primary/90 text-primary-foreground border border-primary rounded-full px-3 py-1.5 font-semibold disabled:opacity-40 ml-auto"
                    >
                      {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                      Enviar ao cliente
                    </button>
                  )}
                </div>

                {/* Aviso quando definitivo já foi entregue */}
                {lead?.care_plan_delivered_at && (
                  <div
                    data-testid="care-plan-delivered-banner"
                    className="mt-3 rounded-lg bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 px-3 py-2 text-[11px] text-[hsl(var(--success))] flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      Plano de Cuidados definitivo entregue ao cliente em{" "}
                      <b>{fmt(lead.care_plan_delivered_at)}</b>
                      {lead.care_plan_delivered_by_name && (
                        <> por <b>{lead.care_plan_delivered_by_name}</b></>
                      )}
                      .
                    </span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <button
          onClick={() => {
            const opening = !historyOpen;
            setHistoryOpen(opening);
            if (opening) loadVersions();
          }}
          data-testid="cp-editor-history-btn"
          className="inline-flex items-center gap-1.5 text-xs bg-card hover:bg-muted border border-border text-muted-foreground rounded-full px-3 py-1.5"
        >
          <History className="w-3.5 h-3.5" /> {historyOpen ? "Fechar histórico" : "Ver histórico"}
        </button>
      </div>

      {historyOpen && (
        <div
          className="mb-6 rounded-xl border border-border bg-muted/40 p-4"
          data-testid="cp-editor-history-panel"
        >
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
            Histórico de versões ({versionsList.length})
          </div>
          <div className="space-y-1.5">
            {versionsList.map((v) => (
              <div
                key={v.id}
                data-testid={`cp-version-row-${v.version_label}`}
                className={`flex items-center gap-3 text-xs rounded-lg px-3 py-2 border ${
                  v.is_current
                    ? "bg-[hsl(var(--info-bg))] border-primary"
                    : "bg-card border-border"
                }`}
              >
                <span className="font-semibold text-primary w-10">
                  {v.version_label}
                </span>
                <span className="flex-1 text-muted-foreground">
                  {v.reason || "—"}
                </span>
                <span className="text-muted-foreground">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {fmt(v.created_at)}
                </span>
                <span className="text-muted-foreground">
                  {v.created_by?.name || "—"}
                  {v.created_by?.role ? ` (${v.created_by.role})` : ""}
                </span>
                {v.is_frozen && (
                  <Lock className="w-3 h-3 text-[hsl(var(--success))]" title="Congelada" />
                )}
                {v.is_current && (
                  <span className="text-[10px] font-semibold text-primary">ACTUAL</span>
                )}
              </div>
            ))}
            {versionsList.length === 0 && (
              <div className="text-xs text-muted-foreground">Sem versões ainda.</div>
            )}
          </div>
        </div>
      )}

      {/* Blocos agrupados por categoria. Cada grupo é colapsável e agrupa
          blocos relacionados (Identificação, Perfil, Objetivos, …). */}
      <div className="space-y-4">
        {BLOCK_GROUPS.map((grp) => {
          const groupBlocks = Object.entries(BLOCK_LABELS).filter(
            ([, spec]) => spec.group === grp.key,
          );
          if (groupBlocks.length === 0) return null;
          return (
            <GroupSection
              key={grp.key}
              group={grp}
              blocks={groupBlocks}
              blocksData={blocks}
              saving={saving}
              disabled={disabled}
              onSave={saveBlock}
              beneficiaries={beneficiaries}
              autofillPreview={autofillPreview}
              canEdit={canEdit}
            />
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="cp-editor-footer">
        <FileText className="w-3.5 h-3.5" />
        {master.current_version_number > 1
          ? `${master.current_version_number} versões no histórico`
          : "Primeira versão"}
        {beneficiaries.length >= 2 && (
          <>
            {" · "}
            <Users className="w-3.5 h-3.5" /> {beneficiaries.length} beneficiários
            ({beneficiaries.map((b) => b.name).join(" · ")})
          </>
        )}
      </div>
    </div>
  );
}
